import * as React from 'react'
import { ReactElement } from 'react'
import InvoiceForm from '../../../components/Form/Finance/Invoice/InvoiceForm'
import {
    InvoiceAction,
    InvoiceDto,
    InvoiceMutationDto,
    LineItemDto,
    LineItemGroupDto,
    LineItemGroupMutationDto,
    LineItemMutationDto,
} from '../../../config/finance/invoiceConfig'
import Loading from '../../../components/Loading'
import { getDataEmptyState } from '../../../context/AxiosContext'
import { useParams } from 'react-router-dom'
import useEntity from '../../../hooks/useEntity'
import { EntityParams } from '../../../components/Form/Form'
import { MessageBar, MessageBarType, Stack, Text } from '@fluentui/react'
import { useDispatch } from 'react-redux'
import { PricePrecedence, PriceSummaryDto } from '../../../config/finance/financeConfig'

const mapPriceSummary = (price?: PriceSummaryDto): string => {
    switch (price?.precedence) {
        case PricePrecedence.Total:
            return price.total
        case PricePrecedence.Subtotal:
            return price.subtotal
        default:
            return undefined
    }
}

const mapInvoice = (invoice: InvoiceDto): InvoiceMutationDto => ({
    id: invoice.id,
    type: invoice.type,
    templateId: invoice.template?.id,
    parentId: invoice.parent?.id,
    status: invoice.status,
    fileId: invoice.file?.id,
    clientPivotId: invoice.recipient?.clientId,
    debtorPivotId: invoice.recipient?.debtorId,
    number: invoice.number,
    name: invoice.name,
    description: invoice.description,
    note: invoice.note,
    date: invoice.date,
    term: invoice.term,
    factor: invoice.factor,
    lineItemGroups: invoice.lineItemGroups?.map(
        (lineItemGroup: LineItemGroupDto): LineItemGroupMutationDto => ({
            id: lineItemGroup.id,
            description: lineItemGroup.description,
            price: mapPriceSummary(lineItemGroup.price),
            precedence: lineItemGroup.price?.precedence,
            showStatement: lineItemGroup.showStatement,
            consolidation: lineItemGroup.consolidation,
            vatType: lineItemGroup.vatType || undefined,
            lineItems: lineItemGroup.lineItems.map(
                (lineItem: LineItemDto): LineItemMutationDto => ({
                    id: lineItem.id,
                    type: lineItem.type,
                    entityId: lineItem.entityId,
                }),
            ),
            accounting: lineItemGroup.accounting,
        }),
    ),
    children: invoice.children?.map((child: InvoiceDto): InvoiceMutationDto => mapInvoice(child)),
    language: invoice.language,
    currency: invoice.price?.currency,
    paymentPrice: invoice.paymentPrice?.amount,
    recipientEmail: invoice.recipient?.email,
    recipientReference: invoice.recipient?.reference,
    messageSubject: invoice.message?.subject,
    messageBody: invoice.message?.body,
    paymentMessageBody: invoice.paymentMessage?.body,
    period: invoice.period,
    unit: invoice.unit,
    accounting: invoice.accounting,
})

export default function EditInvoice(): ReactElement {
    const dispatch = useDispatch()
    const { id } = useParams<EntityParams>()
    const [entity, setEntity] = useEntity<InvoiceDto>((id: string) => ({ url: `/finance/invoice/${id}` }), id)

    if (entity.rejected) {
        dispatch({ type: InvoiceAction.ResetOverview })
        return (
            <MessageBar messageBarType={MessageBarType.error} isMultiline>
                <Stack>
                    <Text variant="smallPlus">De declaratie kon niet geladen worden.</Text>
                    <Text variant="smallPlus">
                        Melding: <em>{entity.error.message}</em>
                    </Text>
                </Stack>
            </MessageBar>
        )
    }

    if (!entity.loaded) {
        return <Loading />
    }

    return (
        <InvoiceForm
            invoice={entity.data}
            formikProps={{
                initialValues: mapInvoice(entity.data),
                onSubmit: () => {
                    return Promise.resolve({
                        url: `/finance/invoice/${id}`,
                        method: 'PUT',
                    })
                },
                onReset: () => setEntity(getDataEmptyState<InvoiceDto>()),
            }}
        />
    )
}
